import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import CustomerSelector from '../../../components/ui/CustomerSelector';
import { supabase } from '../../../lib/supabase';

const BookingForm = ({ 
  booking = null,
  customers = [],
  onSave,
  onCancel,
  onDelete,
  onCreateCustomer,
  selectedDate = null,
  selectedTime = null
}) => {
  const [formData, setFormData] = useState({
    customerId: '',
    date: '',
    time: '',
    duration: '30',
    service: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);

  // Fetch services from Supabase
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoadingServices(true);
        const { data, error } = await supabase?.from('services')?.select('*')?.eq('is_available', true)?.order('name', { ascending: true });
        
        if (error) throw error;
        setServices(data || []);
      } catch (err) {
        console.error('Error fetching services:', err);
        setErrors(prev => ({ ...prev, services: 'Failed to load services' }));
      } finally {
        setLoadingServices(false);
      }
    };
    
    fetchServices();
  }, []);

  useEffect(() => {
    if (booking) {
      setFormData({
        customerId: booking?.customer_id || booking?.customerId || '',
        date: booking?.date || '',
        time: booking?.time || '',
        duration: booking?.duration?.toString() || '30',
        service: booking?.service_id || booking?.service || '',
        notes: booking?.notes || ''
      });
    } else {
      setFormData({
        customerId: '',
        date: selectedDate || new Date()?.toISOString()?.split('T')?.[0],
        time: selectedTime || '09:00',
        duration: '30',
        service: '',
        notes: ''
      });
    }
    setErrors({});
  }, [booking, selectedDate, selectedTime]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.customerId) {
      newErrors.customerId = 'Please select a customer';
    }
    if (!formData?.date) {
      newErrors.date = 'Date is required';
    }
    if (!formData?.time) {
      newErrors.time = 'Time is required';
    }
    if (!formData?.service) {
      newErrors.service = 'Service is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedCustomer = customers?.find(c => c?.id === formData?.customerId);
      await onSave({
        ...formData,
        id: booking?.id || Date.now()?.toString(),
        customerName: selectedCustomer?.name || '',
        customerEmail: selectedCustomer?.email || '',
        customerPhone: selectedCustomer?.phone || '',
        createdAt: booking?.createdAt || new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      });
    } catch (error) {
      setErrors({ submit: 'Failed to save booking. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await onDelete(booking?.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      setErrors({ submit: 'Failed to delete booking. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const durationOptions = [

    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' },
    { value: '150', label: '2.5 hours' },
    { value: '180', label: '3 hours' },
    { value: '210', label: '3.5 hours' },
    { value: '240', label: '4 hours' },
    { value: '270', label: '4.5 hours' },
    { value: '300', label: '5 hours' },
    { value: '330', label: '5.5 hours' },
    { value: '360', label: '6 hours' }

  ];

  // Convert services from database to select options
  const serviceOptions = services?.map(service => ({
    value: service?.id,
    label: service?.name,
    description: service?.description || `${service?.duration} minutes - $${service?.price}`
  }));

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2">
      <div className="px-6 py-5 border-b border-border md:px-8 md:py-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground md:text-2xl">
              {booking ? 'Edit Appointment' : 'New Appointment'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1 md:text-base">
              {booking ? 'Update appointment details' : 'Schedule a new appointment'}
            </p>
          </div>
          {booking && (
            <Button
              variant="destructive"
              size="default"
              iconName="Trash2"
              iconPosition="left"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSubmitting}
              className="hidden md:flex"
            >
              Delete
            </Button>
          )}
        </div>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-6 md:p-8">
        <CustomerSelector
          customers={customers}
          selectedCustomerId={formData?.customerId}
          onSelect={(id) => handleChange('customerId', id)}
          onCreateNew={onCreateCustomer}
          error={errors?.customerId}
          placeholder="Search and select customer..."
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Input
            label="Date"
            type="date"
            value={formData?.date}
            onChange={(e) => handleChange('date', e?.target?.value)}
            error={errors?.date}
            required
          />

          <Input
            label="Time (24h format)"
            type="time"
            value={formData?.time}
            onChange={(e) => handleChange('time', e?.target?.value)}
            error={errors?.time}
            required
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Select
            label="Duration"
            description="Select appointment duration"
            options={durationOptions}
            value={formData?.duration}
            onChange={(value) => handleChange('duration', value)}
            required
          />

          <Select
            label="Service"
            description="Select service type"
            options={serviceOptions}
            value={formData?.service}
            onChange={(value) => handleChange('service', value)}
            error={errors?.service}
            required
            placeholder={loadingServices ? "Loading services..." : "Choose service type..."}
            disabled={loadingServices}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2 md:text-base">
            Notes
          </label>
          <textarea
            value={formData?.notes}
            onChange={(e) => handleChange('notes', e?.target?.value)}
            placeholder="Add any additional notes or special requirements..."
            rows={4}
            className="w-full px-4 py-3 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none md:text-base"
          />
        </div>

        {errors?.submit && (
          <div className="p-4 bg-error/10 border border-error rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error md:text-base">{errors?.submit}</p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 pt-4 border-t border-border md:flex-row md:justify-end">
          {booking && (
            <Button
              type="button"
              variant="destructive"
              fullWidth
              iconName="Trash2"
              iconPosition="left"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSubmitting}
              className="md:hidden"
            >
              Delete Appointment
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={onCancel}
            disabled={isSubmitting}
            className="md:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isSubmitting}
            iconName="Check"
            iconPosition="left"
            className="md:w-auto"
          >
            {booking ? 'Update Appointment' : 'Create Appointment'}
          </Button>
        </div>
      </form>
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-card rounded-xl border border-border shadow-elevation-5 p-6 md:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-error/10 rounded-full flex-shrink-0">
                <Icon name="AlertTriangle" size={24} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2 md:text-xl">
                  Delete Appointment
                </h3>
                <p className="text-sm text-muted-foreground md:text-base">
                  Are you sure you want to delete this appointment? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:justify-end">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isSubmitting}
                className="md:w-auto"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                fullWidth
                loading={isSubmitting}
                iconName="Trash2"
                iconPosition="left"
                onClick={handleDelete}
                className="md:w-auto"
              >
                Delete Appointment
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingForm;